import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useEditor, useNode } from '@splashthat/splash-cms-engine';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import { useFlags } from 'utils/react-context';

import { SessionLabel } from './SessionCardLabel.styles';
import { NODE_DESCENDANT_KEY_SESSION_LABEL } from '../../../../constants';
import {
    SESSION_LABEL_ID,
    SESSION_REGISTRATION_CLOSED_LABEL_ID,
    useSessionRegistrationElementsVisibility,
} from '../../../../../contexts/SessionRegistrationStateProvider';

const BASE_CLASS_NAME = 'SessionCard';
const NO_REGISTRATION_REQUIRED_CONTENT = 'noRegistrationRequiredContent';
const REGISTRATION_CLOSED_CONTENT = 'registrationClosedContent';

/**
 * Decides what key it should use from the label default props meta object.
 * @param visibleElements
 * @returns {string}
 */
function calculateContentKey(visibleElements) {
    if (visibleElements.includes(SESSION_LABEL_ID)) {
        return NO_REGISTRATION_REQUIRED_CONTENT;
    } else if (visibleElements.includes(SESSION_REGISTRATION_CLOSED_LABEL_ID)) {
        return REGISTRATION_CLOSED_CONTENT;
    }
    return '';
}

export const SessionCardLabel = ({ session, styles, meta }) => {
    const { isAgendaSessionRegistrationEnabled } = useFlags();
    const {
        theme: { colors, activeFonts },
        event,
    } = useTouchpointData();
    const editor = useEditor((state) => ({ editing: state.editing }));
    const node = useNode();
    const visibleElements = useSessionRegistrationElementsVisibility({ session, event, editor });

    const contentKey = calculateContentKey(visibleElements);
    const shouldRenderLabel = !!contentKey;

    const setContent = useCallback(
        (text) =>
            node.actions.setProp((editedNode) => {
                // eslint-disable-next-line no-param-reassign
                editedNode.descendants[NODE_DESCENDANT_KEY_SESSION_LABEL].meta[contentKey] = text;
                return editedNode;
            }),
        [],
    );

    if (!isAgendaSessionRegistrationEnabled || !shouldRenderLabel) {
        return null;
    }

    return (
        <SessionLabel
            $fontSize={styles.fontSize}
            $backgroundColor={colors[styles.backgroundColor]}
            $borderColor={colors[styles.borderColor]}
            $fontColor={colors[styles.fontColor]}
            $fontFamily={activeFonts[styles.fontFamily]}
            $borderWidth={styles.borderWidth}
            $borderRadius={styles.borderRadius}
            data-qa={`${BASE_CLASS_NAME}__label`}
        >
            <TextEditable setContent={setContent} disabled={!editor.editing} data-qa={`${BASE_CLASS_NAME}__label_text`}>
                {meta[contentKey]}
            </TextEditable>
        </SessionLabel>
    );
};

SessionCardLabel.propTypes = {
    session: PropTypes.shape({
        id: PropTypes.number,
        registrationRequired: PropTypes.bool,
        waitlist: PropTypes.bool,
        status: PropTypes.string,
    }).isRequired,
    styles: PropTypes.shape({
        fontSize: PropTypes.number,
        backgroundColor: PropTypes.number,
        borderColor: PropTypes.number,
        fontColor: PropTypes.number,
        fontFamily: PropTypes.number,
        borderWidth: PropTypes.number,
        borderRadius: PropTypes.number,
    }).isRequired,
    meta: PropTypes.shape({
        content: PropTypes.string,
    }).isRequired,
};
